import { createContext, useContext, useState } from "react";
import { DocumentSignerAttrTarget, DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";
import { DocumentEntryAndRole, SignEntryAndRole } from "@maven-rest/docs/Folder.dto";
import { v4 as uuidv4 } from "uuid";

export enum MarkerZoneType {
  EConsent = "ECONSENT",
  ESign = "ESIGN",
  None = "NONE",
}

export type LinkingDocumentContextMode = {
  type: "LINKING";
  target: DocumentSignerAttrTarget.Signer;
  signerIndex: number;
  signerUuid: string;
  attr: DocumentSignerAttrType;
  attrUuid: string;
  groupUuid: string;
};

export const defaultUuid = uuidv4();

export type DocumentContextMode = "VIEWER" | "SIGNER" | "SIGNING" | LinkingDocumentContextMode;

interface DocumentContextProps {
  mode: DocumentContextMode;
  setMode: (mode: DocumentContextMode) => void;
  programmatically: boolean;
  isShowValueValidity: boolean;
  setIsShowValueValidity: React.Dispatch<React.SetStateAction<boolean>>;
  documentFile: DocumentEntryAndRole | SignEntryAndRole;
  setDocumentFile: (document: DocumentEntryAndRole | SignEntryAndRole) => void;
  markerZoneType: MarkerZoneType;
  setMarkerZoneType: (markerZoneType: MarkerZoneType) => void;
}

const DocumentContext = createContext<DocumentContextProps>({
  mode: "VIEWER",
  setMode: () => {},
  isShowValueValidity: false,
  setIsShowValueValidity: () => {},
  programmatically: true,
  documentFile: null as unknown as DocumentEntryAndRole,
  setDocumentFile: () => {},
  markerZoneType: MarkerZoneType.None,
  setMarkerZoneType: () => {},
});

function DocumentContextProvider({
  mode: modeProp,
  programmatically: programmaticallyProp,
  documentFile: documentProp,
  markerZoneType: markerZoneProps,
  children,
}: {
  mode: DocumentContextMode;
  programmatically?: boolean;
  documentFile: DocumentEntryAndRole | SignEntryAndRole;
  markerZoneType?: MarkerZoneType;
  children: React.ReactNode;
}) {
  const [mode, setMode] = useState<DocumentContextMode>(modeProp);
  const [programmatically] = useState<boolean>(!!programmaticallyProp);
  const [isShowValueValidity, setIsShowValueValidity] = useState<boolean>(false);

  const [documentFile, setDocumentFile] = useState<DocumentEntryAndRole | SignEntryAndRole>(documentProp);
  const [markerZoneType, setMarkerZoneType] = useState<MarkerZoneType>(markerZoneProps || MarkerZoneType.None);

  return (
    <DocumentContext.Provider
      value={{
        mode,
        setMode,
        programmatically,
        isShowValueValidity,
        setIsShowValueValidity,
        documentFile: documentFile || (null as unknown as DocumentEntryAndRole),
        setDocumentFile,
        markerZoneType,
        setMarkerZoneType,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
}

function useDocumentContext() {
  return useContext(DocumentContext);
}

export { DocumentContext, DocumentContextProvider, useDocumentContext };
